import { useCallback } from 'react'

import { getLoanAllIncludeCategory } from '@mediaingenuity/totallymoney.cardloanutils'

import useAppQueryState from '@hooks/useAppQueryState'
import { isQueryLoading } from '@hooks/utils/isQueryLoading'

import { useLoansDownsellQuery } from '@queries/useLoansDownsellQuery'
import { useScoredLoansQuery } from '@queries/useScoredLoansQuery'

import { getAppConfig } from '@utils/getAppConfig'

interface UseAutoLoanSearchOptions {
    shouldFetchAlternativeProducts: boolean
}

export const useAutoLoanSearch = ({
    shouldFetchAlternativeProducts = false,
}: UseAutoLoanSearchOptions) => {
    const { appQueryState } = useAppQueryState()
    const appConfig = getAppConfig()
    const loansWhitelabel = window.location.pathname.includes('account-initial')
        ? 'account-initial'
        : 'account-latest'
    const loansScoreIdFromUrl = appQueryState.loansScoreId

    const shouldFetchLoansDownsell =
        shouldFetchAlternativeProducts && !loansScoreIdFromUrl

    const loansDownsellResponse = useLoansDownsellQuery({
        customerId: appQueryState.customerId,
        shouldFetch: shouldFetchLoansDownsell,
    })

    const loansScoreId =
        loansScoreIdFromUrl ||
        loansDownsellResponse.data?.loansScoreId ||
        undefined

    const shouldFetchScoredLoans =
        shouldFetchAlternativeProducts && !!loansScoreId

    const scoredLoansResponse = useScoredLoansQuery({
        customerId: appQueryState.customerId,
        scoreId: loansScoreId,
        shouldFetch: shouldFetchScoredLoans,
    })

    const isLoansDownsellLoading = isQueryLoading(
        shouldFetchLoansDownsell,
        loansDownsellResponse
    )

    const isScoredLoansLoading = isQueryLoading(
        shouldFetchScoredLoans,
        scoredLoansResponse
    )

    const hasAvailableLoans =
        shouldFetchAlternativeProducts &&
        scoredLoansResponse.isSuccess &&
        getLoanAllIncludeCategory(
            scoredLoansResponse.data?.scores,
            'Guarantor'
        ) === false &&
        scoredLoansResponse.data?.scores.length !== 0

    const getLoansRedirectUrl = useCallback(() => {
        if (!hasAvailableLoans || !loansScoreId) {
            return null
        }

        const loansUrl = new URL(
            `${appConfig.LOANS_URL}partners/${loansWhitelabel}/all-loans/all/`
        )

        loansUrl.searchParams.set('scoreId', loansScoreId)
        loansUrl.searchParams.set('customerId', appQueryState.customerId!)
        loansUrl.searchParams.set('journey', 'autoLoanSearch')

        return loansUrl.toString()
    }, [
        hasAvailableLoans,
        loansScoreId,
        loansWhitelabel,
        appQueryState.customerId,
        appConfig.LOANS_URL,
    ])

    return {
        getLoansRedirectUrl,
        hasAvailableLoans,
        isLoading: isLoansDownsellLoading || isScoredLoansLoading,
        loansScoreId,
    }
}
